import { useState } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import { Button } from "../../../components/Button";
import { Form } from "../../../components/Form";
import { FormGroup } from "../../../components/FormGroup";
import { FormLabel } from "../../../components/FormLabel";
import { FormActions } from "../../../components/FormActions";
import { Input } from "../../../components/Input";
import { TextArea } from "../../../components/TextArea";
import { SelectInput } from "../../../components/SelectInput";
import styled from "styled-components";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { IntegrationType } from "@KassaroAB/shared/types/enums/IntegrationType";
import { useTranslation } from "react-i18next";
import type {
  IntegrationElavon,
  IntegrationPaytrim,
  RestCreateIntegration,
  RestUpdateIntegration,
} from "@KassaroAB/shared/types/integrations/integration";
import type { StoreId } from "@KassaroAB/shared/types/ids";
import {
  useCreateIntegrationMutation,
  useUpdateIntegrationMutation,
} from "../../../query/integrationsQuery";
import { useGetStoreQuery } from "../../../query/storesQuery";
import { InfoBox } from "../../../components/InfoBox";
import {
  ACQUIRER_FEE_LEVELS,
  ACQUIRER_FEE_LEVEL_OPTIONS,
  findAcquirerFeeLevel,
  type AcquirerFeeLevel,
} from "./acquirerFeeLevels";

const ErrorMessage = styled.div`
  color: ${colors.danger};
  font-size: ${fontSizes.small}px;
  margin-top: ${spacing.x05}px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
  margin-top: ${spacing.x2}px;
`;

const RatesPanel = styled.div`
  padding: ${spacing.x2}px ${spacing.x3}px;
  background-color: ${colors.infoBackground};
  border-radius: ${borderRadius.regular}px;
  border: 1px solid ${colors.info};
`;

const RatesHeading = styled.div`
  font-size: ${fontSizes.small}px;
  font-weight: 700;
  color: ${colors.infoText};
  margin-bottom: ${spacing.x1}px;
`;

const RatesRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${spacing.x2}px;
  padding: ${spacing.x05}px 0;
  font-size: ${fontSizes.small}px;
  color: ${colors.infoText};
`;

const RatesValue = styled.span`
  font-weight: 700;
  white-space: nowrap;
`;

const RatesNote = styled.div`
  margin-top: ${spacing.x1}px;
  font-size: ${fontSizes.small}px;
  color: ${colors.infoText};
`;

const formatPercentage = (percentage: number): string => `${percentage}%`;

const toFeePayload = (feeLevel: AcquirerFeeLevel) => {
  const rates = ACQUIRER_FEE_LEVELS[feeLevel];
  return {
    privateDebitPercentage: rates.privateDebitPercentage,
    privateCreditPercentage: rates.privateCreditPercentage,
    businessPercentage: rates.businessPercentage,
  };
};

type AcquirerIntegrationType =
  | typeof IntegrationType.ELAVON
  | typeof IntegrationType.PAYTRIM;

type AcquirerIntegration = IntegrationElavon | IntegrationPaytrim;

interface AcquirerRequestFormProps {
  storeId: StoreId;
  integrationType: AcquirerIntegrationType;
  integration?: AcquirerIntegration;
  onSuccess: () => void;
  onError: () => void;
  onCancel: () => void;
}

export const AcquirerRequestForm = ({
  storeId,
  integrationType,
  integration,
  onSuccess,
  onError,
  onCancel,
}: AcquirerRequestFormProps) => {
  const { t } = useTranslation();
  const { data: store } = useGetStoreQuery(storeId);
  const { mutate: createMutation } = useCreateIntegrationMutation(
    store?.chainId ?? null,
    storeId,
  );
  const { mutate: updateMutation } = useUpdateIntegrationMutation(store?.chainId ?? null);
  const [requestSent, setRequestSent] = useState(false);

  const defaultName =
    integrationType === IntegrationType.ELAVON ? "Elavon" : "Paytrim";

  const form = useForm({
    defaultValues: {
      name: integration?.name || defaultName,
      contactEmail: integration?.contactEmail || store?.contactEmail || "",
      feeLevel: findAcquirerFeeLevel(
        integration?.fee?.privateDebitPercentage,
      ),
      comment: integration?.comment || "",
    },
    onSubmit: ({ value }) => {
      const fee = toFeePayload(value.feeLevel);
      if (integration) {
        const payload: Partial<RestUpdateIntegration> = {
          type: integrationType,
          name: value.name,
          contactEmail: value.contactEmail,
          fee,
          comment: value.comment,
        };
        updateMutation(
          { integrationId: integration.id, integration: payload },
          { onSuccess: () => onSuccess(), onError: () => onError() },
        );
        return;
      }

      const payload: RestCreateIntegration = {
        type: integrationType,
        name: value.name,
        contactEmail: value.contactEmail,
        fee,
        comment: value.comment,
      };
      createMutation(payload, {
        onSuccess: () => setRequestSent(true),
        onError: () => onError(),
      });
    },
  });

  const feeLevel = useStore(form.store, (state) => state.values.feeLevel);
  const rates = ACQUIRER_FEE_LEVELS[feeLevel];

  if (requestSent && !integration) {
    return (
      <Container>
        <InfoBox variant="success">
          {t("integrationDetails.acquirerRequestSent")}
        </InfoBox>
        <FormActions>
          <Button type="button" onClick={() => onSuccess()}>
            {t("integrationDetails.acquirerRequestDone")}
          </Button>
        </FormActions>
      </Container>
    );
  }

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      {!integration && (
        <FormGroup>
          <InfoBox variant="info">
            {t("integrationDetails.acquirerRequestInfo")}
          </InfoBox>
        </FormGroup>
      )}

      <form.Field
        name="name"
        validators={{
          onChange: ({ value }) =>
            !value ? t("integrationDetails.nameRequired") : undefined,
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("integrationDetails.nameLabel")}</FormLabel>
            <Input
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
              placeholder={t("integrationDetails.namePlaceholder")}
            />
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      <form.Field
        name="contactEmail"
        validators={{
          onChange: ({ value }) => {
            if (!value) return t("integrationDetails.merchantEmailRequired");
            const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
            return ok
              ? undefined
              : t("integrationDetails.merchantEmailInvalid");
          },
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("integrationDetails.merchantEmailLabel")}</FormLabel>
            <Input
              type="email"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
              placeholder={t("integrationDetails.merchantEmailPlaceholder")}
            />
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      <form.Field
        name="feeLevel"
        validators={{
          onChange: ({ value }) =>
            value ? undefined : t("integrationDetails.acquirerFeeLevelRequired"),
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("integrationDetails.acquirerFeeLevelLabel")}</FormLabel>
            <SelectInput
              value={String(field.state.value)}
              onChange={(e) =>
                field.handleChange(Number(e.target.value) as AcquirerFeeLevel)
              }
            >
              {ACQUIRER_FEE_LEVEL_OPTIONS.map((level) => {
                const levelRates = ACQUIRER_FEE_LEVELS[level];
                return (
                  <option key={level} value={level}>
                    {t("integrationDetails.acquirerFeeLevelOption", {
                      level,
                      rate: formatPercentage(
                        levelRates.privateDebitPercentage,
                      ),
                      model: t(
                        `integrationDetails.acquirerFeeModel.${levelRates.model}`,
                      ),
                    })}
                  </option>
                );
              })}
            </SelectInput>
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      <FormGroup>
        <RatesPanel>
          <RatesHeading>
            {t("integrationDetails.acquirerFeeRatesHeading", {
              level: feeLevel,
            })}
          </RatesHeading>
          <RatesRow>
            <span>{t("integrationDetails.acquirerFeePrivateDebit")}</span>
            <RatesValue>
              {formatPercentage(rates.privateDebitPercentage)}
            </RatesValue>
          </RatesRow>
          <RatesRow>
            <span>{t("integrationDetails.acquirerFeePrivateCredit")}</span>
            <RatesValue>
              {formatPercentage(rates.privateCreditPercentage)}
            </RatesValue>
          </RatesRow>
          <RatesRow>
            <span>{t("integrationDetails.acquirerFeeBusiness")}</span>
            <RatesValue>
              {formatPercentage(rates.businessPercentage)}
            </RatesValue>
          </RatesRow>
          <RatesNote>
            {t(`integrationDetails.acquirerFeeModelNote.${rates.model}`)}
          </RatesNote>
          <RatesNote>
            {t("integrationDetails.acquirerFeeShareNote")}
          </RatesNote>
        </RatesPanel>
      </FormGroup>

      <form.Field name="comment">
        {(field) => (
          <FormGroup>
            <FormLabel>{t("integrationDetails.acquirerCommentLabel")}</FormLabel>
            <TextArea
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              placeholder={t("integrationDetails.acquirerCommentPlaceholder")}
            />
          </FormGroup>
        )}
      </form.Field>

      <FormActions>
        <Button type="button" $color="secondary" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
        <Button type="submit" disabled={!store}>
          {integration
            ? t("common.update")
            : t("integrationDetails.acquirerRequestSubmit")}
        </Button>
      </FormActions>
    </Form>
  );
};
